import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { SMM_API_ENDPOINTS as cfg } from './config';
import { getSmmError, isSmmError } from './errors';
import { smmApi } from './smm-api';
import type {
  PresignSocialPostMediaResponse,
  RegisterSocialPostMediaResponse,
} from './types.generated';

/** Image types assistant-smm accepts for social post media. */
export const SOCIAL_POST_MEDIA_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
] as const;

export type SocialPostMediaType = (typeof SOCIAL_POST_MEDIA_TYPES)[number];

/** Largest upload assistant-smm accepts (its default `S3_MAX_UPLOAD_BYTES`, 10 MiB). */
export const SOCIAL_POST_MEDIA_MAX_BYTES = 10 * 1024 * 1024;

/** Most images one post can carry (X allows four). */
export const SOCIAL_POST_MEDIA_MAX_COUNT = 4;

/** Connect/TTFB allowance of the upload, before any of its bytes move. */
const UPLOAD_CONNECT_ALLOWANCE_MS = 20_000;

/**
 * Slowest uplink the upload deadline tolerates: 64 B/ms is ~500 kbit/s, a poor
 * mobile link. A full {@link SOCIAL_POST_MEDIA_MAX_BYTES} image therefore gets
 * ~2.7 min of transfer on top of {@link UPLOAD_CONNECT_ALLOWANCE_MS}.
 */
const UPLOAD_MIN_THROUGHPUT_BYTES_PER_MS = 64;

/**
 * How long the direct upload of a `sizeBytes` image to storage may take before
 * it is given up. Without a deadline a stalled upload never settles.
 *
 * Scaled by size rather than one flat deadline: `fetch` cannot report upload
 * progress, so a deadline is the only bound available, and one generous enough
 * for a 10 MB image would leave a thumbnail wedging the form for minutes.
 *
 * @internal Exported for this package's own tests, deliberately not in the
 * barrel: no consumer reasons about the deadline, they react to `S3_TIMEOUT`.
 */
export const getSocialPostMediaUploadTimeoutMs = (sizeBytes: number): number =>
  UPLOAD_CONNECT_ALLOWANCE_MS + Math.ceil(sizeBytes / UPLOAD_MIN_THROUGHPUT_BYTES_PER_MS);

/** Client-side validation failure of {@link validateSocialPostMedia}. */
export type SocialPostMediaValidationCode = 'MEDIA_TYPE' | 'MEDIA_TOO_LARGE' | 'MEDIA_EMPTY';

/**
 * Error thrown by {@link uploadSocialPostMedia} before anything reaches the
 * network (invalid file) or when the direct S3 upload fails. API errors of the
 * presign/register calls are rethrown as RTK Query errors instead; normalise
 * either with `getSmmError`.
 */
export class SocialPostMediaUploadError extends Error {
  override readonly name = 'SocialPostMediaUploadError';

  constructor(
    message: string,
    /**
     * Validation code, `S3_UPLOAD` when storage rejected the upload, or
     * `S3_TIMEOUT` when it did not answer within the upload timeout.
     */
    readonly code: SocialPostMediaValidationCode | 'S3_UPLOAD' | 'S3_TIMEOUT',
    /** HTTP status of the failed S3 upload. */
    readonly status?: number,
  ) {
    super(message);
  }
}

const isMediaType = (type: string): type is SocialPostMediaType =>
  (SOCIAL_POST_MEDIA_TYPES as readonly string[]).includes(type);

/**
 * Checks a file against the server's media rules (type, size), so obviously
 * invalid files are refused without a presign round-trip.
 *
 * @returns The validation code, or `undefined` when the file is acceptable.
 */
export const validateSocialPostMedia = (
  file: Pick<File, 'type' | 'size'>,
): SocialPostMediaValidationCode | undefined => {
  if (!isMediaType(file.type)) return 'MEDIA_TYPE';
  if (file.size <= 0) return 'MEDIA_EMPTY';
  if (file.size > SOCIAL_POST_MEDIA_MAX_BYTES) return 'MEDIA_TOO_LARGE';

  return undefined;
};

/** Options of {@link uploadSocialPostMedia}. */
export interface UploadSocialPostMediaOptions {
  /** Aborts the presign, the S3 upload and the registration. */
  signal?: AbortSignal;
}

/**
 * Result of {@link uploadSocialPostMedia}: the registered media, or `null` when
 * a retry found the key already registered (a lost 201) — the image is attached
 * either way, and the invalidated post refetch shows it.
 */
export type UploadSocialPostMediaResult = RegisterSocialPostMediaResponse | null;

type SmmThunkDispatch = ThunkDispatch<unknown, unknown, UnknownAction>;

const throwIfAborted = (signal?: AbortSignal) => {
  signal?.throwIfAborted();
};

/** A request that never got an HTTP response (network failure, timeout). */
const isTransportError = (error: unknown) => {
  const { status } = getSmmError(error);

  return status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR';
};

/**
 * Whether a registration failed because the key is already attached.
 *
 * A 409 without a code (backends before SKY-4778) only counts on the retry
 * after a lost response, where the first attempt registering this freshly
 * presigned key is the one plausible conflict; anywhere else it is rethrown.
 */
const isAlreadyRegistered = (error: unknown, isRetry: boolean) => {
  const { status, code } = getSmmError(error);

  return (
    isSmmError(error, 'social-post.exceptions.CONFLICT_MEDIA_REGISTERED') ||
    (isRetry && status === 409 && code === undefined)
  );
};

/**
 * Forwards an abort of `signal` to `abort`, so an inner request with its own
 * cancellation stops with the caller.
 *
 * Wired by hand rather than with `AbortSignal.any`, which is Chrome 116+ /
 * Safari 17.4+ / Firefox 124+ — above the floor the dashboard builds for,
 * where `['defaults', 'not IE 11']` still resolves to Chrome 109 and the
 * legacy plugin's core-js polyfills do not cover `AbortSignal` statics.
 *
 * @returns A cleanup removing the listener, for the caller's `finally`.
 */
const linkAbort = (signal: AbortSignal | undefined, abort: () => void) => {
  signal?.addEventListener('abort', abort, { once: true });

  return () => {
    signal?.removeEventListener('abort', abort);
  };
};

/** Runs an RTK Query mutation thunk, aborting it together with `signal`. */
const runAbortable = async <T>(
  request: { unwrap: () => Promise<T>; abort: () => void },
  signal?: AbortSignal,
): Promise<T> => {
  const unlink = linkAbort(signal, () => {
    request.abort();
  });
  try {
    return await request.unwrap();
  } catch (error) {
    // RTK rejects an aborted request with its own `AbortError`-shaped object;
    // surface the signal's reason instead, as every other abort path does.
    if (signal?.aborted) throw signal.reason;
    throw error;
  } finally {
    unlink();
  }
};

/**
 * POSTs the file to the presigned S3 URL: every presigned field, then `file`
 * last. Given up after `timeoutMs` with an `S3_TIMEOUT` error; an abort of
 * `signal` (the caller leaving) still rejects with the signal's reason.
 */
const postToStorage = async (
  { url, fields }: PresignSocialPostMediaResponse,
  file: File,
  timeoutMs: number,
  signal?: AbortSignal,
) => {
  // An abort that already happened reaches no listener below, so it is what
  // keeps a cancelled upload off the network.
  signal?.throwIfAborted();

  const form = new FormData();
  for (const [name, value] of Object.entries(fields)) {
    form.append(name, String(value));
  }
  // S3 ignores every field after `file`, so it must come last.
  form.append('file', file);

  // One controller for both reasons to stop: the caller's signal (linked
  // below) and our own deadline. The deadline is a plain `setTimeout` rather
  // than `AbortSignal.timeout` — that one is in range for the browsers we
  // build for (Chrome 103+), but it runs on a clock `vi.useFakeTimers` cannot
  // drive, and the tests have to reach the deadline without waiting it out.
  const upload = new AbortController();
  const unlink = linkAbort(signal, () => {
    upload.abort(signal?.reason);
  });
  const timer = setTimeout(() => {
    upload.abort();
  }, timeoutMs);
  let response: Response;
  try {
    // No auth header: the presigned policy is the credential, and S3 rejects
    // (and CORS preflights) an unexpected Authorization header.
    response = await fetch(url, { method: 'POST', body: form, signal: upload.signal });
  } catch (error) {
    // The caller's abort comes first: it also aborts `upload`, so only the
    // signal itself tells the two apart.
    if (signal?.aborted) throw signal.reason;
    if (upload.signal.aborted) {
      throw new SocialPostMediaUploadError(
        `Uploading ${file.name} to storage timed out after ${timeoutMs} ms`,
        'S3_TIMEOUT',
      );
    }
    throw error;
  } finally {
    clearTimeout(timer);
    unlink();
  }
  if (!response.ok) {
    throw new SocialPostMediaUploadError(
      `Uploading ${file.name} to storage failed with status ${response.status}`,
      'S3_UPLOAD',
      response.status,
    );
  }
};

/**
 * Thunk uploading one image to a draft post: presign → direct multipart POST to
 * S3 → register the uploaded key.
 *
 * - Validates type and size first ({@link validateSocialPostMedia}).
 * - A registration that fails without a response is retried once; a
 *   `CONFLICT_MEDIA_REGISTERED` then means the first attempt did register the key, so it resolves with `null` instead of failing.
 * - Registration invalidates the post, so views refetch the new media.
 *
 * @example
 * ```ts
 * const controller = new AbortController();
 * await dispatch(uploadSocialPostMedia(post.id, file, { signal: controller.signal }));
 * ```
 *
 * @throws {SocialPostMediaUploadError} on an invalid file, a failed S3 upload,
 *   or one that ran past {@link getSocialPostMediaUploadTimeoutMs} (`S3_TIMEOUT`).
 * @throws The RTK Query error of a failed presign/registration (see `getSmmError`).
 * @throws The signal's reason when aborted.
 */
export const uploadSocialPostMedia =
  (postId: number, file: File, { signal }: UploadSocialPostMediaOptions = {}) =>
  async (dispatch: SmmThunkDispatch): Promise<UploadSocialPostMediaResult> => {
    const contentType = file.type;
    const invalid = validateSocialPostMedia(file);
    if (invalid || !isMediaType(contentType)) {
      throw new SocialPostMediaUploadError(
        `${file.name} is not an acceptable image`,
        invalid ?? 'MEDIA_TYPE',
      );
    }
    throwIfAborted(signal);

    const ticket = await runAbortable(
      dispatch(
        smmApi.endpoints[cfg.presignSocialPostMedia.generated].initiate({
          id: postId,
          presignSocialPostMediaBody: {
            contentType,
            sizeBytes: file.size,
          },
        }),
      ),
      signal,
    );

    throwIfAborted(signal);
    await postToStorage(ticket, file, getSocialPostMediaUploadTimeoutMs(file.size), signal);
    throwIfAborted(signal);

    const register = async () =>
      runAbortable(
        dispatch(
          smmApi.endpoints[cfg.registerSocialPostMedia.generated].initiate({
            id: postId,
            registerSocialPostMediaBody: { key: ticket.key },
          }),
        ),
        signal,
      );

    let isRetry = false;
    try {
      try {
        return await register();
      } catch (error) {
        // The response may have been lost after the server registered the key:
        // retry once, and let a 409 below tell us it did.
        if (!isTransportError(error) || signal?.aborted) throw error;
        isRetry = true;
        return await register();
      }
    } catch (error) {
      if (isAlreadyRegistered(error, isRetry)) {
        // The registration mutation itself failed, so its tags did not fire.
        dispatch(smmApi.util.invalidateTags([{ type: 'SocialPost', id: postId }]));
        return null;
      }
      throw error;
    }
  };
